import { create } from 'zustand';
import type { Tenant, User } from '@/types/api';
import { STORAGE_KEYS } from '@/config/constants';

function safeParse<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

interface AuthState {
  token: string | null;
  tenant: Tenant | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  onboardingComplete: boolean;
}

interface AuthActions {
  setAuth: (token: string, tenant: Tenant | null, user: User | null) => void;
  updateTenant: (tenant: Tenant) => void;
  updateUser: (userData: Partial<User>) => void;
  setOnboardingComplete: (val: boolean) => void;
  clearAuth: () => void;
  setLoading: (isLoading: boolean) => void;
  initAuth: () => void;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>((set, get) => ({
  token: localStorage.getItem(STORAGE_KEYS.TOKEN) || null,
  tenant: safeParse<Tenant>(STORAGE_KEYS.TENANT),
  user: safeParse<User>(STORAGE_KEYS.USER),
  isAuthenticated: !!localStorage.getItem(STORAGE_KEYS.TOKEN),
  isLoading: true,
  onboardingComplete: false,

  setAuth: (token: string, tenant: Tenant | null, user: User | null) => {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    tenant
      ? localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenant))
      : localStorage.removeItem(STORAGE_KEYS.TENANT);
    user
      ? localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
      : localStorage.removeItem(STORAGE_KEYS.USER);
    set({ token, tenant, user, isAuthenticated: true, isLoading: false });
  },

  updateTenant: (tenant: Tenant) => {
    localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenant));
    set({ tenant });
  },

  updateUser: (userData: Partial<User>) => {
    const current = get().user;
    if (!current) return;
    const updated: User = { ...current, ...userData };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
    set({ user: updated });
  },

  setOnboardingComplete: (val: boolean) => set({ onboardingComplete: val }),

  clearAuth: () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TENANT);
    localStorage.removeItem(STORAGE_KEYS.USER);
    set({ token: null, tenant: null, user: null, isAuthenticated: false, isLoading: false, onboardingComplete: false });
  },

  setLoading: (isLoading: boolean) => set({ isLoading }),

  initAuth: () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && !isTokenExpired(token)) {
      set({
        tenant: safeParse<Tenant>(STORAGE_KEYS.TENANT),
        user: safeParse<User>(STORAGE_KEYS.USER),
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      if (token) {
        get().clearAuth();
      } else {
        set({ isAuthenticated: false, isLoading: false });
      }
    }
  },
}));

export default useAuthStore;
